import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const EMPTY_FORM = {
  name: "", address: "", postcode: "", contact_name: "", contact_job_title: "",
  contact_email: "", contact_phone: "", contract_reference: "", po_notes: "",
  service_start_date: "", service_end_date: "", status: "pending", notes: "", contacts: [],
};

const STATUS_STYLES = {
  active: "bg-ukgreen-light text-ukgreen border-transparent",
  inactive: "bg-slate-100 text-slate-600 border-transparent",
  pending: "bg-amber-100 text-amber-800 border-transparent",
};

export default function Councils() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("councils:manage");
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/councils", { params });
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", address: c.address || "", postcode: c.postcode || "",
      contact_name: c.contact_name || "", contact_job_title: c.contact_job_title || "",
      contact_email: c.contact_email || "", contact_phone: c.contact_phone || "",
      contract_reference: c.contract_reference || "", po_notes: c.po_notes || "",
      service_start_date: c.service_start_date || "", service_end_date: c.service_end_date || "",
      status: c.status || "pending", notes: c.notes || "", contacts: c.contacts || [],
    });
    setDialogOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.contacts = form.contacts.filter((c) => c.name);
      if (editing) {
        await api.patch(`/councils/${editing.id}`, payload);
        toast.success("Local authority updated");
      } else {
        await api.post("/councils", payload);
        toast.success("Local authority created");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setContact = (i, key, value) => {
    const contacts = [...form.contacts];
    contacts[i] = { ...contacts[i], [key]: value };
    setForm({ ...form, contacts });
  };

  return (
    <div data-testid="councils-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Local Authorities</h1>
          <p className="text-sm text-slate-600 mt-1">Commissioning councils and local authority contracts.</p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditing(null); setForm(EMPTY_FORM); } }}>
            <DialogTrigger asChild>
              <Button data-testid="council-add-button" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Add local authority
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "Add local authority"}</DialogTitle>
                <DialogDescription>Record the council's contract, contacts and service dates.</DialogDescription>
              </DialogHeader>
              <form onSubmit={onSave} className="space-y-4" data-testid="council-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2"><Label>Council / local authority name *</Label>
                    <Input data-testid="council-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Address</Label>
                    <Input data-testid="council-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Postcode</Label>
                    <Input data-testid="council-postcode-input" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Main contact name</Label>
                    <Input data-testid="council-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact job title</Label>
                    <Input data-testid="council-contact-title-input" value={form.contact_job_title} onChange={(e) => setForm({ ...form, contact_job_title: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact email</Label>
                    <Input data-testid="council-contact-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact telephone</Label>
                    <Input data-testid="council-contact-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contract / framework reference</Label>
                    <Input data-testid="council-reference-input" value={form.contract_reference} onChange={(e) => setForm({ ...form, contract_reference: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="council-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active" data-testid="council-status-active">Active</SelectItem>
                        <SelectItem value="inactive" data-testid="council-status-inactive">Inactive</SelectItem>
                        <SelectItem value="pending" data-testid="council-status-pending">Pending</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Service start date</Label>
                    <Input data-testid="council-start-input" type="date" value={form.service_start_date} onChange={(e) => setForm({ ...form, service_start_date: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Service end date</Label>
                    <Input data-testid="council-end-input" type="date" value={form.service_end_date} onChange={(e) => setForm({ ...form, service_end_date: e.target.value })} /></div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Authorised contacts</Label>
                    <Button type="button" variant="outline" className="h-9" data-testid="council-add-contact-button"
                      onClick={() => setForm({ ...form, contacts: [...form.contacts, { name: "", job_title: "", email: "", phone: "", is_primary: form.contacts.length === 0 }] })}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add contact
                    </Button>
                  </div>
                  {form.contacts.map((c, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 rounded-md border border-border p-3" data-testid={`council-contact-row-${i}`}>
                      <Input placeholder="Name" value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
                      <Input placeholder="Job title" value={c.job_title || ""} onChange={(e) => setContact(i, "job_title", e.target.value)} />
                      <Input placeholder="Email" type="email" value={c.email || ""} onChange={(e) => setContact(i, "email", e.target.value)} />
                      <Input placeholder="Phone" value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} />
                      <Button type="button" variant="ghost" className="h-9 text-red-600" data-testid={`contact-remove-${i}`}
                        onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, j) => j !== i) })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="space-y-1.5"><Label>Purchase order requirements / notes</Label>
                  <Textarea data-testid="council-po-input" rows={2} value={form.po_notes} onChange={(e) => setForm({ ...form, po_notes: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea data-testid="council-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  <p className="text-xs text-slate-500">Do not store passwords or unnecessary confidential council information here.</p></div>
                <DialogFooter>
                  <Button data-testid="council-save-button" type="submit" disabled={saving}
                    className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create local authority"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="council-search-input" placeholder="Search by name or reference…"
            value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="council-status-filter" className="w-full sm:w-48 h-11"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="council-filter-all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-600" data-testid="councils-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading local authorities…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-sm text-slate-600" data-testid="councils-empty">No local authorities found.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="councils-grid">
          {items.map((c) => (
            <Card key={c.id} data-testid={`council-card-${c.council_number}`} className="hover:border-primary/40 transition-colors duration-200">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-light">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 leading-tight">{c.name}</p>
                      <p className="font-mono text-xs text-slate-500">{c.council_number}</p>
                    </div>
                  </div>
                  <Badge className={STATUS_STYLES[c.status]} data-testid={`council-status-${c.council_number}`}>{c.status}</Badge>
                </div>
                <dl className="text-sm space-y-1 text-slate-600">
                  {c.contract_reference && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Ref</dt><dd>{c.contract_reference}</dd></div>}
                  {c.contact_name && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Contact</dt><dd>{c.contact_name}{c.contact_job_title ? ` — ${c.contact_job_title}` : ""}</dd></div>}
                  {c.contact_email && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Email</dt><dd className="break-all">{c.contact_email}</dd></div>}
                  <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Contacts</dt><dd>{(c.contacts || []).length} authorised</dd></div>
                </dl>
                {canManage && (
                  <Button variant="outline" className="w-full h-10" data-testid={`council-edit-${c.council_number}`} onClick={() => openEdit(c)}>
                    Edit record
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
